'use client';
import React, { useEffect, useRef, useState } from 'react';

const HeroSection: React.FC = () => {
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Counter animation: 0 → 4.2 over ~1.5s
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          let start = 0;
          const end = 4.2;
          const duration = 1400;
          const step = 16;
          const increment = (end / (duration / step));
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(parseFloat(start.toFixed(1)));
            }
          }, step);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="min-h-screen flex flex-col items-center justify-center pt-24 pb-16 px-6 relative">
      {/* Background radial glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(0,229,204,0.07) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto text-center">
        {/* Badge */}
        <div
          className={`inline-flex items-center gap-2 glass-panel px-4 py-2 mb-8 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-teal teal-pulse" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-[#C0C7D0] font-medium">
            Live in 165,000+ US Auto Repair Shops
          </span>
        </div>

        {/* Headline */}
        <h1
          className={`heading font-black leading-[0.9] mb-6 transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ fontSize: 'clamp(2.8rem, 8vw, 7rem)' }}
        >
          <span className="text-white">Repair order.</span>
          <br />
          <span className="teal-gradient-text">Sent invoice.</span>
        </h1>

        {/* Sub */}
        <p
          className={`text-[#C0C7D0] text-lg md:text-xl max-w-2xl mx-auto mb-14 leading-relaxed transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          No handwritten tickets. No midnight DMS exports. No missed line items.
          Just one tap from the shop floor to a paid invoice.
        </p>

        {/* HERO STAT — the proof card */}
        <div
          className={`inline-block glass-panel teal-glow-box p-8 md:p-12 mb-14 transition-all duration-700 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ minWidth: '340px' }}
        >
          <div className="mono text-[4.5rem] md:text-[6rem] font-bold teal-gradient-text leading-none">
            {count.toFixed(1)}
            <span className="text-[2rem] md:text-[3rem] text-[rgba(0,229,204,0.6)] ml-1">s</span>
          </div>
          <div className="text-[#C0C7D0] text-base md:text-lg mt-3 font-medium">
            Average time from repair order to sent invoice
          </div>
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-teal teal-pulse" />
            <span className="text-[11px] tracking-widest text-[rgba(192,199,208,0.5)] uppercase">
              Measured across 14,000+ shops
            </span>
          </div>
        </div>

        {/* CTAs */}
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-400 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <a href="#calculator" className="btn-teal px-8 py-4 text-[15px] w-full sm:w-auto text-center">
            Run Your Shop&apos;s Numbers
          </a>
          <a href="#demo" className="btn-ghost px-8 py-4 text-[15px] w-full sm:w-auto text-center">
            See It Beat Your Current Setup
          </a>
        </div>

        {/* Trust line */}
        <p
          className={`mt-8 text-[12px] text-[rgba(192,199,208,0.4)] tracking-wide transition-all duration-700 delay-500 ${
            visible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          Start Free — No Card. No DMS Migration. No Commitment.
        </p>
      </div>
    </section>
  );
};

export default HeroSection;